/* eslint-disable max-statements */
import React, { ComponentType, ReactNode } from 'react';
import compose from 'lodash/fp/compose';
import { DispatchMetrics } from '@catalogo/core-metrics';
import { useQuery, useApolloClient } from '@apollo/client';
import { withDevice } from '@catalogo/core-connect/device';
import { withProductQuantity } from '@catalogo/service-americanas-product/src/hocs/product-quantity';
import {
  selectButtonFlow,
  getUrl,
  getBuyButtonText,
} from '@catalogo/service-americanas-product/src/helpers/buy-button';
import { getSelectedStore } from '@catalogo/service-americanas-product/src/queries/store.graphql';
import { ToggleBuyTogether } from '@catalogo/service-americanas-product/src/payment/buy-together-vip.graphql';
import { withMetrics } from '@catalogo/core-metrics';
import { createMainOffer } from '@catalogo/core-metrics/src/helpers';
import { getPayments } from '@catalogo/service-americanas-product/src/payment/payments';
import { withCookies } from '@catalogo/core-connect/cookies';
import { infoForWarranty } from '@catalogo/service-americanas-product/src/helpers/services-pdp';
import { sendMetrics } from '../helpers/buy-button';

interface WithBuyButtonProps {
  children: ReactNode;
  isO2O: boolean;
  product: {
    name: string;
  };
  categoryUrl: string;
  currentSku: {
    id: string;
    offers: {
      result: {
        seller: {
          id: string;
          name: string;
        };
        currency: string;
      }[];
    };
  };
  productId: string;
  isPreSale: boolean;
  fullWidth: boolean;
  productQty: number;
  experiment: string;
  device: string;
  sellerStoreId: string;
  dispatchMetrics: DispatchMetrics;
  selectQuantityWasUsed: number;
  routerProps: string;
  discountProgressiveExperiment: string;
  setCookie(): void;
  servicePDPinfoForWarranty: infoForWarranty;
  history: unknown;
}

// eslint-disable-next-line max-lines-per-function
export const withBuyButton = (Component: ComponentType) => (props: WithBuyButtonProps) => {
  const {
    isO2O,
    product,
    categoryUrl,
    currentSku,
    productId,
    isPreSale,
    fullWidth = true,
    productQty,
    experiment,
    device,
    sellerStoreId,
    dispatchMetrics,
    selectQuantityWasUsed,
    routerProps,
    discountProgressiveExperiment,
    setCookie,
    servicePDPinfoForWarranty,
    history,
  } = props;
  const { data } = useQuery(getSelectedStore);
  const { data: buyTogether } = useQuery(ToggleBuyTogether);

  let currentSellerStoreId = sellerStoreId;

  if (!currentSellerStoreId) {
    currentSellerStoreId = isO2O ? data?.selectedStore?.sellerStoreId : null;
  }

  const warrantyUrl = getUrl(
    productId,
    currentSku,
    currentSellerStoreId,
    buyTogether,
    productQty,
    servicePDPinfoForWarranty
  );

  const url = selectButtonFlow(
    isO2O,
    currentSellerStoreId,
    currentSku,
    warrantyUrl,
    experiment,
    discountProgressiveExperiment
  );
  const isInternational = currentSku?.offers?.result?.[0]?.currency === 'USD';

  const client = useApolloClient();
  const mainOffer = createMainOffer({ routerProps: routerProps, client: client, body: {} });
  const { mainPayment } = getPayments(mainOffer);

  const currentProduct = {
    id: productId,
    name: product?.name,
    price: mainPayment?.price,
    quantity: productQty,
    category: categoryUrl,
    sku: currentSku?.id,
    sellerId: currentSku?.offers?.result?.[0]?.seller?.id,
    sellerName: currentSku?.offers?.result?.[0]?.seller?.name,
    hasProggresiveDiscount: discountProgressiveExperiment?.toLowerCase() === 'new',
  };

  const withBuyButtonProps = {
    isInternational,
    url,
    fullWidth,
    buyButtonText: getBuyButtonText(isPreSale),
    showPreSaleButton: isPreSale && device === 'mobile',
    sendMetrics: sendMetrics(
      dispatchMetrics,
      selectQuantityWasUsed,
      servicePDPinfoForWarranty,
      url,
      setCookie,
      [currentProduct],
      history
    ),
  };

  return <Component {...props} {...withBuyButtonProps} />;
};

export default compose(withDevice, withProductQuantity, withMetrics, withCookies, withBuyButton);
